import { Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Autocomplete, CircularProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { addNewWorkerRoleSuggestion, clonePayRateToAllProjectsOfClient, deleteWorkRole, editWorkRoleSuggestion, GetAllWorkRole, getRateTypes } from "../../../api/apiCall";
import { getAllPayRates, createPayRate, updatePayRate, deletePayRate } from "../../../api/apiCall";
import { getAllClients, getProjectsByCompanyId } from "../../../api/apiCall"; 
import { useAuth } from "../../../auth/auth";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { Modal } from "antd";
import { Delete, Edit, ContentCopy } from "@mui/icons-material";

const WorkRoleSuggestions = () => {
    const { state } = useAuth();
    const [workRole, setWorkRole] = useState<any>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [currentWorkRole, setCurrentWorkRole] = useState<any>(null);
    const [workRoleName, setWorkRoleName] = useState('');
    const [formError, setFormError] = useState('');
    const [payRateTypes, setPayRateTypes] = useState<any[]>([]);
    const [selectedPayRateId, setSelectedPayRateId] = useState<number | null>(null);
    const [, setOpen] = useState(false);

    // Client / Project filter state
    const [clients, setClients] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [selectedClient, setSelectedClient] = useState<any>(null);
    const [selectedProject, setSelectedProject] = useState<any>(null);
    const [projectsLoading, setProjectsLoading] = useState(false);

    // Pay Rate Management State
    const [payRateModalOpen, setPayRateModalOpen] = useState(false);
    const [selectedWorkRoleForPayRates, setSelectedWorkRoleForPayRates] = useState<any>(null);
    const [payRates, setPayRates] = useState<any[]>([]);
    const [payRateFormMode, setPayRateFormMode] = useState<'add' | 'edit'>('add');
    const [currentPayRate, setCurrentPayRate] = useState<any>(null);
    const [chargeRateValue, setChargeRateValue] = useState('');
    const [payRateFormError, setPayRateFormError] = useState('');
    const [chargeRateFormError, setChargeRateFormError] = useState('');
    const [payRateSubDialogOpen, setPayRateSubDialogOpen] = useState(false);

    // ========== Fetch clients on mount ==========
    useEffect(() => {
        const fetchClients = async () => {
            try {
                const response = await getAllClients(state.user?.token);
                setClients(response || []);
            } catch (error) {
                toast.error('Failed to load clients');
            }
        };
        fetchClients();
    }, [state.user?.token]);

    // ========== Fetch projects when client changes ==========
    useEffect(() => {
        if (!selectedClient) {
            setProjects([]);
            setSelectedProject(null);
            return;
        }
        const fetchProjects = async () => {
            setProjectsLoading(true);
            try {
                const response = await getProjectsByCompanyId(state.user?.token, selectedClient.id);
                setProjects(response || []);
            } catch (error) {
                toast.error('Failed to load projects for this client');
                setProjects([]);
            } finally {
                setProjectsLoading(false);
            }
        };
        fetchProjects();
    }, [selectedClient, state.user?.token]);

    // ========== Existing handlers (unchanged) ==========
    const handleAddClick = () => {
        setModalMode('add');
        setWorkRoleName('');
        setCurrentWorkRole(null);
        setFormError('');
        setIsDialogOpen(true);
    };

    const handleEditClick = (params: any) => {
        setModalMode('edit');
        setCurrentWorkRole(params.row);
        setWorkRoleName(params.row.workRoleName);
        setFormError('');
        setIsDialogOpen(true);
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setWorkRoleName('');
        setCurrentWorkRole(null);
        setFormError('');
    };

    const getAllPayRateTypes = async () => {
        try {
            const response = await getRateTypes(state.user?.token);
            setPayRateTypes(response.data);
        } catch (error) {
            toast.error('Failed to load rate types');
        }
    };

    const handleSubmit = async () => {
        if (!workRoleName.trim()) {
            setFormError('Work role name cannot be empty');
            return;
        }

        if (modalMode === 'add') {
            const response = await addNewWorkerRoleSuggestion(state.user?.token, workRoleName);
            if (response.success) {
                toast.success(response.message);
                GetAllWorkRole(state.user?.token).then(setWorkRole);
                handleDialogClose();
            } else if (response === 403) {
                toast.error('You are not authorized to perform this action.');
                handleDialogClose();
            } else {
                toast.error('Something went wrong!');
                handleDialogClose();
            }
        } else {
            if (currentWorkRole) {
                const response = await editWorkRoleSuggestion(state.user?.token, currentWorkRole.id, workRoleName);
                if (response.success) {
                    toast.success(response.message);
                    GetAllWorkRole(state.user?.token).then(setWorkRole);
                    handleDialogClose();
                } else if (response === 403) {
                    toast.error('You are not authorized to perform this action.');
                    handleDialogClose();
                } else {
                    toast.error('Something went wrong!');
                }
            }
        }
    };

    const handleDelete = async (id: any) => {
        const response = await deleteWorkRole(state.user?.token, id);
        if (response) {
            toast.success(response.data);
            GetAllWorkRole(state.user?.token).then(setWorkRole);
        } else {
            toast.error('Something went wrong!');
        }
    };

    // ===================== Pay Rate Handlers =====================
    const openPayRateModal = async (workRole: any) => {
        setSelectedWorkRoleForPayRates(workRole);
        setPayRateModalOpen(true);
        try {
            const rates = await getAllPayRates(state.user?.token, selectedProject.id, workRole.id);
            setPayRates(rates.data);
        } catch (error) {
            toast.error('Failed to load pay rates');
        }
    };

    const closePayRateModal = () => {
        setPayRateModalOpen(false);
        setSelectedWorkRoleForPayRates(null);
        setPayRates([]);
        setPayRateFormMode('add');
        setCurrentPayRate(null);
        setChargeRateValue('');
        setPayRateFormError('');
        setChargeRateFormError('');
        setSelectedPayRateId(null);
    };

    const handleAddPayRate = () => {
        setPayRateFormMode('add');
        setCurrentPayRate(null);
        setChargeRateValue('');
        setPayRateFormError('');
        setChargeRateFormError('');
        setSelectedPayRateId(null);
        setPayRateSubDialogOpen(true);
    };

    const cloneRates = async (rate: any) => {
        await clonePayRateToAllProjectsOfClient(state.user?.token, {
            workRoleId: selectedWorkRoleForPayRates.id,clientId: selectedClient.id,projectId: selectedProject.id,payRateId: rate.id
        })
    };

    const handleEditPayRate = (payRate: any) => {
        setPayRateFormMode('edit');
        setCurrentPayRate(payRate);
        setChargeRateValue(payRate.chargeRate?.toString() || ''); // Set charge rate
        setPayRateFormError('');
        setChargeRateFormError('');
        setSelectedPayRateId(payRate.rateTypeId || null); // Adjust field name if needed
        setPayRateSubDialogOpen(true);
    };

    const handleDeletePayRate = async (payRateId: number) => {
        Modal.confirm({
            title: 'Delete Pay Rate',
            content: 'Are you sure you want to delete this pay rate?',
            onOk: async () => {
                const response = await deletePayRate(state.user?.token, payRateId);
                if (response.success) {
                    toast.success('Pay rate deleted successfully');
                    const rates = await getAllPayRates(state.user?.token, selectedProject.id, selectedWorkRoleForPayRates.id);
                    setPayRates(rates.data);
                } else {
                    toast.error('Failed to delete pay rate');
                }
            },
            okButtonProps: { danger: true },
            okText: 'YES',
            cancelText: 'NO',
            centered: true,
        });
    };

    const handlePayRateSubDialogClose = () => {
        setPayRateSubDialogOpen(false);
        setPayRateFormMode('add');
        setCurrentPayRate(null);
        setChargeRateValue('');
        setPayRateFormError('');
        setChargeRateFormError('');
        setSelectedPayRateId(null);
    };

    const handlePayRateSubmit = async () => {
        // Validate Charge Rate
        if (!chargeRateValue.trim() || isNaN(Number(chargeRateValue)) || Number(chargeRateValue) <= 0) {
            setChargeRateFormError('Please enter a valid positive number for charge rate');
            return;
        } else {
            setChargeRateFormError('');
        }

        if (!selectedPayRateId) {
            setPayRateFormError('Please select a rate type');
            return;
        }

        const payload = {
            rateTypeId: selectedPayRateId,
            projectId: selectedProject.id,
            chargeRate: parseFloat(chargeRateValue),
            effectiveDate: new Date().toISOString(),
            isActive: true,
            workRoleId: selectedWorkRoleForPayRates.id,
            currency: 'GBP',
        };

        try {
            let response;
            if (payRateFormMode === 'add') {
                response = await createPayRate(state.user?.token, payload);
            } else {
                response = await updatePayRate(state.user?.token, currentPayRate.id, payload);
            }
            if (response.success) {
                toast.success(response.message || 'Rate saved');
                const rates = await getAllPayRates(state.user?.token, selectedProject.id, selectedWorkRoleForPayRates.id);
                setPayRates(rates.data);
                handlePayRateSubDialogClose();
            } else {
                toast.error('Failed to save rate');
            }
        } catch (error) {
            toast.error('Something went wrong');
        }
    };

    // ===================== Columns =====================
    const columns: any = [
        {
            field: 'workRoleName',
            headerName: 'Work Role',
            flex: 1,
        },
        {
            field: 'createdDate',
            headerName: 'Created Date',
            flex: 0.3,
            renderCell: (params: any) => (
                <div>{format(new Date(params.row.createdDate || Date.now()), 'dd/MM/yyyy')}</div>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.5,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <div>
                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={() => openPayRateModal(params.row)}
                        size="small"
                        style={{ marginRight: 8, color: '#6c757d', borderColor: '#6c757d', boxShadow: 'none' }}
                        disabled={!selectedClient || !selectedProject}
                    >
                        Rates
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => handleEditClick(params)}
                        size="small"
                        style={{ marginRight: 8, backgroundColor: '#6c757d', boxShadow: 'none' }}
                    >
                        Edit
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        size="small"
                        style={{ boxShadow: 'none' }}
                        onClick={() => {
                            setOpen(true);
                            Modal.confirm({
                                title: `Are you sure you want to delete ${params.row.workRoleName}?`,
                                onOk: () => handleDelete(params.id),
                                okButtonProps: { danger: true },
                                okText: 'YES',
                                cancelText: 'NO',
                                centered: true,
                            });
                        }}
                    >
                        Delete
                    </Button>
                </div>
            ),
        },
    ];

    useEffect(() => {
        GetAllWorkRole(state.user?.token).then(setWorkRole);
        getAllPayRateTypes();
    }, []);

    const selectedPayRateType = payRateTypes.find((type) => type.id === selectedPayRateId) || null;

    return (
        <Box
            sx={{
                p: 3,
                borderRadius: '16px',
                backgroundColor: '#ffffff',
                maxWidth: '1200px',
                margin: '0 auto',
            }}
        >
            {/* ========== Client / Project Filter Bar ========== */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Autocomplete
                        options={clients}
                        getOptionLabel={(option: any) => option.companyName || ''}
                        isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
                        style={{ minWidth: 250 }}
                        value={selectedClient}
                        onChange={(_, value) => setSelectedClient(value)}
                        clearOnEscape
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Client"
                                placeholder="Choose a Client"
                                size="small"
                            />
                        )}
                    />

                    <Autocomplete
                        options={projects}
                        loading={projectsLoading}
                        getOptionLabel={(option: any) =>
                            option.projectName
                                ? `${option.projectName}${option.workerCount ? ` - (${option.workerCount} Workers)` : ''}`
                                : ''
                        }
                        isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
                        style={{ minWidth: 250 }}
                        value={selectedProject}
                        onChange={(_, value) => setSelectedProject(value)}
                        disabled={!selectedClient}
                        clearOnEscape
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Project"
                                placeholder={
                                    projectsLoading
                                        ? 'Loading projects...'
                                        : projects.length === 0 && selectedClient
                                        ? 'No projects available for this client'
                                        : 'Choose a project (optional)'
                                }
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {projectsLoading && <CircularProgress color="inherit" size={20} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddClick}
                    style={{ boxShadow: 'none' }}
                >
                    Add Work Role
                </Button>
            </Box>

            {/* DataGrid */}
            <Box
                sx={{
                    margin: '0 auto',
                    height: '735px',
                    '@media (max-width: 900px)': {
                        overflowX: 'auto',
                        margin: 1,
                        minWidth: '95%',
                    },
                }}
            >
                <DataGrid
                    rows={workRole}
                    columns={columns}
                    disableRowSelectionOnClick
                    sx={{
                        '@media (max-width: 700px)': {
                            minWidth: '100%',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            color: '#333',
                            fontWeight: 'bold',
                            fontSize: 15,
                            backgroundColor: '#f0f0f0',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 'bold',
                        },
                        '@media (max-width: 600px)': {
                            fontSize: '0.7rem',
                        },
                    }}
                />
            </Box>

            {/* ========== Dialogs ========== */}

            {/* Work Role Add/Edit Dialog */}
            <Dialog
                open={isDialogOpen}
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {modalMode === 'add' ? 'Add New Work Role' : 'Edit Work Role'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        size="small"
                        margin="dense"
                        label="Work Role Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={workRoleName}
                        onChange={(e) => {
                            setWorkRoleName(e.target.value);
                            setFormError('');
                        }}
                        error={!!formError}
                        helperText={formError}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSubmit();
                            }
                        }}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained" style={{ boxShadow: 'none' }}>
                        {modalMode === 'add' ? 'Add' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Pay Rate Management Modal */}
            <Dialog
                open={payRateModalOpen}
                onClose={closePayRateModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Pay & Charge Rates for "{selectedWorkRoleForPayRates?.workRoleName || ''}"
                    {selectedClient && selectedProject && (
                        <span style={{ fontSize: '0.8rem', fontWeight: 'normal', display: 'block', marginTop: 4 }}>
                            Client: {selectedClient.companyName} | Project: {selectedProject.projectName}
                        </span>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddPayRate}
                            style={{ boxShadow: 'none', marginRight: 8 }}
                        >
                            Add Rate
                        </Button>
                    </Box>
                    <List>
                        {payRates.length === 0 ? (
                            <ListItem>
                                <ListItemText primary="No rates defined for this work role." />
                            </ListItem>
                        ) : (
                            payRates?.map((rate) => (
                                <ListItem key={rate.id} divider>
                                    <ListItemText
                                        primary={`Charge: £${rate.chargeRate}`}
                                        secondary={`${rate.name} rate`}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton edge="end" onClick={() => cloneRates(rate)} color="primary">
                                            <ContentCopy />
                                        </IconButton>
                                        <IconButton edge="end" onClick={() => handleEditPayRate(rate)} color="primary">
                                            <Edit />
                                        </IconButton>
                                        <IconButton edge="end" onClick={() => handleDeletePayRate(rate.id)} color="error">
                                            <Delete />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))
                        )}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePayRateModal} color="inherit">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Pay Rate Sub‑Dialog */}
            <Dialog
                open={payRateSubDialogOpen}
                onClose={handlePayRateSubDialogClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {payRateFormMode === 'add' ? 'Add Rate' : 'Edit Rate'}
                </DialogTitle>
                <DialogContent>
                    <Autocomplete
                        style={{ marginTop: 6 }}
                        size="small"
                        options={payRateTypes}
                        value={selectedPayRateType}
                        onChange={(_, newValue) => {
                            setSelectedPayRateId(newValue ? newValue.id : null);
                            setPayRateFormError('');
                            setChargeRateFormError('');
                        }}
                        getOptionLabel={(option: any) => option.name || ""}
                        renderOption={(props, option) => (
                            <ListItem {...props} key={option.name}>
                                {option.name}
                            </ListItem>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Rate Type"
                                placeholder="Select Rate Type"
                                error={!!payRateFormError && !selectedPayRateId}
                                helperText={payRateFormError && !selectedPayRateId ? payRateFormError : ''}
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                    />

                    <TextField
                        size="small"
                        margin="dense"
                        label="Charge Rate Amount (£)"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={chargeRateValue}
                        onChange={(e) => {
                            setChargeRateValue(e.target.value);
                            setChargeRateFormError('');
                        }}
                        error={!!chargeRateFormError && chargeRateFormError.includes('charge')}
                        helperText={chargeRateFormError && chargeRateFormError.includes('charge') ? chargeRateFormError : ''}
                        inputProps={{ step: "0.01", min: "0" }}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePayRateSubDialogClose} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handlePayRateSubmit} color="primary" variant="contained" style={{ boxShadow: 'none' }}>
                        {payRateFormMode === 'add' ? 'Add' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WorkRoleSuggestions;
